import { combine, split } from 'shamir-secret-sharing';
import { RECOVERY_BLOCK_DAYS, RECOVERY_DELAY_HOURS, SHARE_THRESHOLD, SHARE_TOTAL, VERIFY_CODE_DIGITS, } from '@fh/shared';
import { alsPuffer, b64 } from './suite.js';
/**
 * Der soziale Recovery-Anker (ADR-008): Shamir 2-aus-3.
 *
 * Der Recovery-Key wird **auf dem Gerät** in drei Anteile geteilt. Jeder Anteil
 * wird für einen Verwahrer verschlüsselt; der Server speichert nur Ciphertext
 * und kann selbst mit allen dreien nichts anfangen.
 *
 * Was die Bibliothek NICHT leistet: Ein manipulierter Anteil führt nicht zu
 * einem Fehler, sondern zu einem stillschweigend falschen Ergebnis. Deshalb
 * verlangt ADR-008 eine Authentifizierung — sie steht unten als `pruefwert`.
 * Ohne sie bekäme der Nutzer im Ernstfall einen unbrauchbaren Schlüssel und
 * keinen Hinweis, woran es lag.
 */
export { RECOVERY_BLOCK_DAYS, RECOVERY_DELAY_HOURS, SHARE_THRESHOLD, SHARE_TOTAL };
const PRUEF_INFO = 'family-hub/recovery-anchor/v1';
async function hmacSchluessel(geheim) {
    // Der Prüfschlüssel wird aus dem Geheimnis abgeleitet, nicht daneben
    // gespeichert — sonst wäre er ein zweites Geheimnis, das verloren gehen kann.
    const material = await crypto.subtle.importKey('raw', alsPuffer(geheim), 'HKDF', false, ['deriveKey']);
    return crypto.subtle.deriveKey({
        name: 'HKDF', hash: 'SHA-256',
        salt: new Uint8Array(0),
        info: new TextEncoder().encode(PRUEF_INFO),
    }, material, { name: 'HMAC', hash: 'SHA-256', length: 256 }, false, ['sign', 'verify']);
}
async function pruefwertVon(geheim) {
    const key = await hmacSchluessel(geheim);
    const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(PRUEF_INFO));
    return b64.encode(new Uint8Array(sig));
}
/** Teilt einen Schlüssel in drei Anteile, von denen zwei genügen. */
export async function anteileErzeugen(geheim) {
    if (geheim.length < 16)
        throw new AnkerFehler('Der Schlüssel ist zu kurz');
    const roh = await split(geheim, SHARE_TOTAL, SHARE_THRESHOLD);
    return {
        anteile: roh.map((a) => b64.encode(a)),
        pruefwert: await pruefwertVon(geheim),
    };
}
/**
 * Setzt den Schlüssel aus mindestens zwei Anteilen wieder zusammen.
 *
 * Prüft das Ergebnis gegen den Prüfwert. Stimmt es nicht, war ein Anteil falsch
 * oder verfälscht — und der Nutzer bekommt das gesagt, statt mit einem
 * unbrauchbaren Schlüssel weiterzumachen.
 */
export async function anteileZusammensetzen(anteile, pruefwert) {
    if (anteile.length < SHARE_THRESHOLD) {
        throw new AnkerFehler(`Es werden ${SHARE_THRESHOLD} Anteile gebraucht, vorhanden sind ${anteile.length}`);
    }
    let geheim;
    try {
        geheim = await combine(anteile.map((a) => b64.decode(a)));
    }
    catch {
        throw new AnkerFehler('Die Anteile passen nicht zusammen');
    }
    if (await pruefwertVon(geheim) !== pruefwert) {
        // Genau der Fall, für den ADR-008 die Authentifizierung verlangt: Shamir
        // selbst würde hier stillschweigend Müll liefern.
        throw new AnkerFehler('Die Anteile passen nicht zusammen');
    }
    return geheim;
}
export class AnkerFehler extends Error {
}
// --- Verifikationsnummer -----------------------------------------------------
/**
 * Sechsstellige Zahl, die Verwahrer telefonisch abgleichen sollen (ADR-008).
 *
 * Sie ist der Schutz gegen einen Server, der Wiederherstellungsanfragen fälscht:
 * Wer die Zahl nicht vom Betroffenen selbst gehört hat, soll nicht zustimmen.
 * Die UI sagt das in einem Satz.
 */
export function verifikationsnummer() {
    const bytes = crypto.getRandomValues(new Uint8Array(4));
    const wert = ((bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3]) >>> 0;
    return String(wert % 10 ** VERIFY_CODE_DIGITS).padStart(VERIFY_CODE_DIGITS, '0');
}
// --- Anteile für Verwahrer verschlüsseln -------------------------------------
/**
 * Verschlüsselt einen Anteil für genau einen Empfänger.
 *
 * Der Server bekommt nur das Ergebnis. Selbst mit allen drei Anteilen in der
 * Datenbank kann er nichts rekonstruieren — er hat keinen der privaten
 * Schlüssel, mit denen sie geöffnet würden.
 */
export async function anteilVerschluesseln(anteil, empfaengerPublicKey) {
    const ct = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, empfaengerPublicKey, alsPuffer(b64.decode(anteil)));
    return b64.encode(new Uint8Array(ct));
}
export async function anteilEntschluesseln(paket, eigenerPrivateKey) {
    const klar = await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, eigenerPrivateKey, alsPuffer(b64.decode(paket)));
    return b64.encode(new Uint8Array(klar));
}
/**
 * Schlüsselpaar eines Verwahrers.
 *
 * RSA-OAEP statt ECIES: WebCrypto kann RSA-OAEP direkt verschlüsseln, für
 * elliptische Kurven müsste man ECDH plus eine eigene Kapselung bauen — und
 * genau das ist der Eigenbau, den dieses Projekt vermeidet. Der Anteil ist
 * wenige Dutzend Byte, die Größe von RSA spielt hier keine Rolle.
 */
export async function verwahrerSchluesselpaar() {
    const paar = await crypto.subtle.generateKey({ name: 'RSA-OAEP', modulusLength: 3072, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' }, true, ['encrypt', 'decrypt']);
    const spki = await crypto.subtle.exportKey('spki', paar.publicKey);
    return { publicKey: b64.encode(new Uint8Array(spki)), privateKey: paar.privateKey };
}
export function verwahrerPublicKeyLaden(spkiB64) {
    return crypto.subtle.importKey('spki', alsPuffer(b64.decode(spkiB64)), { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['encrypt']);
}
/**
 * Verliert ein Verwahrer sein Gerät, sinkt die Zahl einlösbarer Anteile. Unter
 * der Schwelle ist der Anker wertlos — das soll auffallen, bevor man ihn
 * braucht, nicht danach.
 */
export function ankerZustand(aktiveAnteile) {
    if (aktiveAnteile === 0)
        return 'nicht_eingerichtet';
    if (aktiveAnteile >= SHARE_TOTAL)
        return 'ok';
    if (aktiveAnteile >= SHARE_THRESHOLD)
        return 'gefaehrdet';
    return 'unbrauchbar';
}
export function ankerHinweis(zustand) {
    switch (zustand) {
        case 'nicht_eingerichtet':
            return 'Noch nicht eingerichtet. Zwei Angehörige könnten dir dann gemeinsam wieder Zugang verschaffen.';
        case 'ok':
            return 'Eingerichtet. Zwei deiner drei Vertrauenspersonen können dir gemeinsam wieder Zugang verschaffen.';
        case 'gefaehrdet':
            return 'Ein Anteil fehlt. Es reicht noch, aber richte den Anker bald neu ein.';
        case 'unbrauchbar':
            return 'Es sind zu wenige Anteile übrig — der Anker funktioniert nicht mehr. Bitte neu einrichten.';
    }
}
//# sourceMappingURL=anker.js.map